import axios from "axios";
import React, { useEffect, useState } from "react";
import { api } from "../utils/Api";
import {
	Typography,
	Flex,
	Button,
	notification,
	Statistic,
	Modal,
	Upload,
	Input,
	Radio,
	Tag,
} from "antd";
import { CustomTable } from "../utils/CustomComponents";
import {
	EditOutlined,
	RetweetOutlined,
	UploadOutlined,
} from "@ant-design/icons";
const { Text, Title } = Typography;

const QAQCJobLoadsExternalModal = ({
	modalOpenLoads,
	setModalOpenLoads,
	selectedJob,
	setSelectedJob,
}) => {
	const [loadingTable, setLoadingTable] = useState(false);
	const [loadList, setLoadList] = useState([]);
	const [apiNotification, contextHolder] = notification.useNotification();

	const [refresh, setRefresh] = useState(false);

	const [editingRow, setEditingRow] = useState(null);
	const [draftRow, setDraftRow] = useState({});

	const openNotification = (type, message, description) => {
		apiNotification[type]({
			message: message,
			description: description,
		});
	};

	/**
	 ** Options header (columns)
	 */
	const columnsLoads = [
		{
			title: "Delivery Ticket",
			dataIndex: "deliveryTicket",
			key: "deliveryTicket",
			render: (value) => {
				return <Text>{value}</Text>;
			},
		},
		{
			title: "Truck",
			dataIndex: "truck",
			key: "truck",
			render: (value) => {
				return <Text>{value}</Text>;
			},
		},
		{
			title: "Load (YD3)",
			dataIndex: "yards",
			key: "yards",
			render: (value) => {
				return (
					<Statistic
						value={value}
						valueStyle={{
							fontSize: 14,
						}}
					/>
				);
			},
		},
		{
			title: "Sample Type and Size",
			dataIndex: "pourTestingCylinderSize",
			key: "pourTestingCylinderSize",
			render: (value) => {
				return <Text>{value}</Text>;
			},
		},
		{
			title: "Laboratory Test Results",
			dataIndex: "laboratoryTestResults",
			key: "laboratoryTestResults",
			render: (value = [], record) => {
				const isEditing = editingRow === record.id;

				if (isEditing) {
					return (
						<Upload
							beforeUpload={() => false}
							maxCount={10}
							multiple
							accept=".png,.jpg,.jpeg,.pdf"
							fileList={draftRow.laboratoryTestResults || []}
							onChange={({ fileList }) => onChangeEvidence(fileList)}
						>
							<Button
								icon={<UploadOutlined />}
								size="large"
								style={{ width: "100%" }}
							>
								Upload
							</Button>
						</Upload>
					);
				}

				if (value && value.length > 0) {
					return (
						<Flex vertical gap={4}>
							{value.map((file, index) => {
								const fileName = file.split("/")[8];
								return (
									<Tag color={index % 2 === 0 ? "blue" : "cyan"}>
										<a
											key={index}
											href={file}
											target="_blank"
											rel="noopener noreferrer"
										>
											{fileName}
										</a>
									</Tag>
								);
							})}
						</Flex>
					);
				}

				return <Text type="secondary">No files added</Text>;
			},
		},
		{
			title: "Comment",
			dataIndex: "comment",
			key: "comment",
			render: (value, record) => {
				const disabled = editingRow !== record.id;

				return (
					<Input.TextArea
						value={editingRow === record.id ? draftRow.comment : value}
						onChange={onChangeComment}
						disabled={disabled}
					/>
				);
			},
		},
		{
			title: "Compliance",
			dataIndex: "compliance",
			key: "compliance",
			render: (value, record) => {
				const disabled = editingRow !== record.id;

				return (
					<Radio.Group
						value={editingRow === record.id ? draftRow.compliance : value}
						onChange={onChangeCompliance}
						disabled={disabled}
						options={[
							{ value: true, label: "Yes" },
							{ value: false, label: "No" },
						]}
					/>
				);
			},
		},
		{
			title: "Actions",
			key: "actions",
			render: (_, record) => {
				if (editingRow === record.id) {
					return (
						<Flex gap={8}>
							<Button type="primary" onClick={() => onSave(record)}>
								Save
							</Button>
							<Button onClick={onCancel}>Cancel</Button>
						</Flex>
					);
				}

				return (
					<Button
						icon={<EditOutlined />}
						type="primary"
						onClick={() => onEdit(record)}
						disabled={editingRow !== null}
					/>
				);
			},
		},
	];

	const onEdit = (record) => {
		setEditingRow(record.id);
		setDraftRow({
			comment: record.comment || "",
			compliance: record.compliance ?? undefined,
			laboratoryTestResults: [],
		});
	};

	const onCancel = () => {
		setEditingRow(null);
		setDraftRow({});
	};

	const onSave = async (record) => {
		await addEvidence(
			{
				comment: draftRow.comment,
				compliance: draftRow.compliance,
				laboratoryTestResults: draftRow.laboratoryTestResults,
			},
			record.id,
		);

		setEditingRow(null);
		setDraftRow({});
	};

	const onChangeComment = (e) => {
		setDraftRow((prev) => ({ ...prev, comment: e.target.value }));
	};

	const onChangeCompliance = (e) => {
		setDraftRow((prev) => ({ ...prev, compliance: e.target.value }));
	};

	const onChangeEvidence = (fileList) => {
		setDraftRow((prev) => ({ ...prev, laboratoryTestResults: fileList }));
	};

	useEffect(() => {
		if (selectedJob) getLoadList();
	}, [refresh, selectedJob]);

	const getLoadList = async () => {
		try {
			setLoadingTable(true);
			const url = api + `/job/get/qc/laboratory/loads/${selectedJob.id}`;

			const token = localStorage.getItem("token");
			axios.defaults.headers.common = { Authorization: `Bearer ${token}` };

			const { data } = await axios.get(url);
			if (data.success) {
				let list = [];
				data.payload.map((item, index) => {
					list.push({
						key: index,
						id: item._id,
						deliveryTicket: item.deliveryTicket,
						truck: item.truck.number,
						yards: item.yards,
						pourTestingCylinderSize: item.qcData.pourTestingCylinderSize,
						laboratoryTestResults: item.qcData.laboratoryTestResultsEvidence,
						comment: item.qcData.externalComment,
						compliance: item.qcData.compliance,
					});
				});
				setLoadList(list);
				setLoadingTable(false);
			} else {
				setLoadingTable(false);
				openNotification("error", "Error", data.message);
			}
		} catch (err) {
			console.error(err.message);
			openNotification("error", "Error", err.message);
			return null;
		}
	};

	const addEvidence = async (values, loadId) => {
		try {
			setLoadingTable(true);

			const formData = new FormData();

			if (
				values.laboratoryTestResults &&
				values.laboratoryTestResults.length > 0
			) {
				values.laboratoryTestResults.forEach((file) => {
					formData.append("evidence", file.originFileObj);
				});
			}

			formData.append("externalComment", values.comment);
			formData.append("compliance", values.compliance);

			const url = api + `/job/load/add/qc/external/evidence/${loadId}`;
			const token = localStorage.getItem("token");
			axios.defaults.headers.common = {
				Authorization: `Bearer ${token}`,
			};
			const config = {
				headers: {
					"Content-Type": "multipart/form-data",
				},
			};

			const { data } = await axios.post(url, formData, config);
			if (data.success) {
				openNotification("success", "Success", data.message);
				setRefresh((prev) => !prev);
			} else {
				openNotification("error", "Error", data.message);
				setLoadingTable(false);
			}
		} catch (err) {
			setLoadingTable(false);
			openNotification("error", "Error", "Error while trying to add evidence");
			console.error(err.message);
			return null;
		}
	};

	return (
		<Modal
			title={`Job ID: ${selectedJob?.number}`}
			open={modalOpenLoads}
			onCancel={() => {
				setSelectedJob(null);
				setModalOpenLoads(false);
			}}
			footer={null}
			width={1500}
			centered
		>
			<Flex vertical gap={16}>
				<Title level={4}>Registered Samples</Title>
				<Button
					type="primary"
					onClick={() => setRefresh((prev) => !prev)}
					icon={<RetweetOutlined />}
					size="large"
					loading={loadingTable}
				/>
				<CustomTable
					columns={columnsLoads}
					data={loadList}
					loading={loadingTable}
				/>
			</Flex>
			{contextHolder}
		</Modal>
	);
};

export default QAQCJobLoadsExternalModal;
